import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next';
import Image from "next/legacy/image";
import { useRef } from 'react';
import CareerGuideShortInfo from '../../../components/careerguide/CareerGuideShortInfo';
import CareerInfo from '../../../components/careers/CareerInfo';
import Heart from '../../../components/icons/Heart';
import Footer from '../../../components/wherewego/Footer';
import careerPageGetSSP from '../../../lib/gssp/careerPageGetSSP';
import useCareerConnection from '../../../lib/hooks/careerConnection';
import { useAuth } from '../../../lib/providers/authProvider';
import Head from 'next/head';
import CareerJobAndWageInfo from '../../../components/shared/CareerJobAndWageInfo';
import EducationPathways from '../../../components/careerguide/EducationPathways';
import LocalEmployers from '../../../components/careerguide/LocalEmployers';
import GNOHeader from '../../../components/wherewego/Header';
import frontendURL from '../../../lib/config/project/frontendURL';
import { siteTitle } from '../../../lib/config/project/meta';
import initUser from '../../../lib/hooks/initUser';

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  return careerPageGetSSP(ctx);
}

const CareerGuidePage: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ career, serverUser, serverSeeker }) => {
  initUser(serverUser, serverSeeker);
  const { seeker } = useAuth();
  const careerRef = useRef<HTMLDivElement>(null);
  const { connection, handleClick } = useCareerConnection(
    career,
    seeker,
    careerRef,
  );

  return (
    <>
      <Head>
        <title>{`${career.title} | ${siteTitle}`}</title>
        <meta
          name="description"
          content={career.shortDescription.substring(0, 160)}
        />
        <meta property="og:title" content={`${career.title} | ${siteTitle}`} />
        <meta property="og:description" content={career.shortDescription.substring(0, 160)} />
        <meta property="og:type" content="article" />
        {career.video?.poster && (
          <meta property="og:image" content={career.video.poster} />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Occupation',
              name: career.title,
              description: career.shortDescription,
              occupationalCategory: career.onetCode,
              estimatedSalary: career.wageData?.medianAnnualSalary
                ? {
                    '@type': 'MonetaryAmountDistribution',
                    currency: 'USD',
                    median: career.wageData.medianAnnualSalary,
                  }
                : undefined,
              qualifications: career.eduation,
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: frontendURL },
                { '@type': 'ListItem', position: 2, name: 'Explore Careers', item: `${frontendURL}/careerguide` },
                { '@type': 'ListItem', position: 3, name: career.title },
              ],
            }),
          }}
        />
      </Head>
      <GNOHeader />
      <section className="px-8 pt-10 pb-20 bg-careerPageHeading md:pb-40">
        <section className="mx-auto max-w-[900px] relative">
          <img
            src="/images/blue-dots-bg.svg"
            className="absolute top-0 left-0 hidden md:block"
            alt="Blue Dots"
          />

          <figure className="relative md:p-8">
            <Image
              className="rounded-2xl md:rounded-careerImg h-[225px] md:h-[400px] object-cover w-full"
              src={career.video?.poster || ''}
              blurDataURL={career.video?.poster}
              layout="responsive"
              loading="eager"
              placeholder="blur"
              width={1050}
              height={475}
              alt={career?.title}
            />
          </figure>
          <section className="mx-4 my-4 md:my-8 md:mx-10">
            <section className="grid grid-cols-1 md:grid-cols-careerInfo md:gap-16">
              <section>
                <h1 className="text-3xl font-medium text-white md:text-5xl leading-heading">
                  {career.title}
                </h1>
                <p className="my-2 text-base leading-6 text-white md:text-xl md:my-8 md:leading-8">
                  {career.shortDescription}
                </p>
              </section>
              <section>
                <button
                  onClick={handleClick}
                  className="grid grid-cols-2 fill-white hover:fill-careerPageHighlight items-center content-center my-4 md:my-0 mx-auto md:mx-0 block rounded-md bg-careerPageHighlight hover:bg-white border-2 border-solid border-careerPageHighlight p-4 text-white hover:text-careerPageHighlight font-base font-medium  w-[222px] h-[71px] md:h-[90px] text-lg"
                >
                  <Heart
                    className={`h-6 w-6 mx-auto ${
                      connection ? 'fill-inherit' : ''
                    }`}
                  />
                  <p className="text-left">{connection ? 'Saved' : 'Save'}</p>
                </button>
                <CareerInfo career={career} />
              </section>
            </section>
            <section className="grid gap-8 my-4 md:grid-cols-careerInfo">
              <CareerGuideShortInfo career={career} />
            </section>
          </section>
        </section>
      </section>
      <section className="p-10 px-8 bg-off-gray">
        <section className="mx-auto max-w-[900px]">
          <CareerJobAndWageInfo career={career} />
          <EducationPathways career={career} />
          <LocalEmployers career={career} />
        </section>
      </section>
      <Footer />
    </>
  );
};

export default CareerGuidePage;
